import { lazy, Suspense } from "react"
import { AuthGate } from "@/auth"
import { HomePage } from "@/pages/home/home"
import { RootScreen } from "@/pages/root"
import { UnauthorizedPage } from "@/pages/unauthorized"
import type { RouteObject } from "react-router-dom"

import { PageLoadingFallback } from "@be-miniapp/ui/components/ui/spinner"
import { RouteEnterContent } from "@be-miniapp/ui/components/route-enter-content"

// Lazy-loaded route components — each becomes its own chunk
const VerificationPage = lazy(() =>
  import("@/pages/verification/page").then((m) => ({
    default: m.VerificationPage,
  }))
)
const EditPickupPage = lazy(() =>
  import("@/pages/ride/edit-pickup").then((m) => ({
    default: m.EditPickupPage,
  }))
)
const FarePage = lazy(() =>
  import("@/pages/ride/fare/fare").then((m) => ({ default: m.FarePage }))
)
const PaymentMethodPage = lazy(() =>
  import("@/pages/ride/payment-method").then((m) => ({
    default: m.PaymentMethodPage,
  }))
)
const PromoSelect = lazy(() =>
  import("@/pages/ride/promo-select").then((m) => ({ default: m.PromoSelect }))
)
const RideRating = lazy(() =>
  import("@/pages/ride/ride-rating").then((m) => ({ default: m.RideRating }))
)
const SelectRoute = lazy(() =>
  import("@/pages/ride/select-route").then((m) => ({ default: m.SelectRoute }))
)
const SelectRouteMap = lazy(() =>
  import("@/pages/ride/select-route-map").then((m) => ({
    default: m.SelectRouteMap,
  }))
)
const NonSupportedPage = lazy(() =>
  import("@/pages/non-supported").then((m) => ({ default: m.NonSupportedPage }))
)
const CancelEngagement = lazy(() =>
  import("@/pages/ride/cancel-engagement").then((m) => ({
    default: m.CancelEngagement,
  }))
)
const InTripPreloadScreen = lazy(() =>
  import("@/pages/ride/in-trip/preload-page").then((m) => ({
    default: m.InTripPreloadScreen,
  }))
)
const InvoiceInformationScreen = lazy(() =>
  import("@/pages/ride/invoice-information").then((m) => ({
    default: m.InvoiceInformationScreen,
  }))
)
const ConfirmAddress = lazy(() =>
  import("@/pages/ride/confirm-address/confirm-address").then((m) => ({
    default: m.ConfirmAddress,
  }))
)
const FareOptionScreen = lazy(() =>
  import("@/pages/ride/fare-option").then((m) => ({
    default: m.FareOptionScreen,
  }))
)
const UserSettingsPage = lazy(() =>
  import("@/pages/user/settings").then((m) => ({ default: m.UserSettingsPage }))
)
const UserSettingsLanguagePage = lazy(() =>
  import("@/pages/user/settings/language").then((m) => ({
    default: m.UserSettingsLanguagePage,
  }))
)
const UserSupportScreen = lazy(() =>
  import("@/pages/user/support").then((m) => ({ default: m.UserSupportScreen }))
)
const TransportDetailScreen = lazy(() =>
  import("@/pages/activities/transport-detail").then((m) => ({
    default: m.TransportDetailScreen,
  }))
)
const UserPaymentMethodScreen = lazy(() =>
  import("@/pages/user/payment").then((m) => ({
    default: m.UserPaymentMethodScreen,
  }))
)
const EditUserLocationPage = lazy(() =>
  import("@/pages/user/edit-location").then((m) => ({
    default: m.EditUserLocationPage,
  }))
)
const RideContractScreen = lazy(() =>
  import("@/pages/ride/contract").then((m) => ({
    default: m.RideContractScreen,
  }))
)
const ChatScreen = lazy(() =>
  import("@/pages/ride/chat").then((m) => ({ default: m.ChatScreen }))
)
const PromoDetailPage = lazy(() =>
  import("@/pages/ride/promo-detail").then((m) => ({
    default: m.PromoDetailPage,
  }))
)
const FinishRide = lazy(() =>
  import("@/pages/ride/finish-ride").then((m) => ({ default: m.FinishRide }))
)
const SaveAddressFormScreen = lazy(() =>
  import("@/pages/ride/save-address/form-page").then((m) => ({
    default: m.SaveAddressFormScreen,
  }))
)
const AddSaveAddressScreen = lazy(() =>
  import("@/pages/ride/save-address/page").then((m) => ({
    default: m.AddSaveAddressScreen,
  }))
)
const InRideTripPage = lazy(() =>
  import("@/pages/ride/in-trip/page").then((m) => ({
    default: m.InRideTripPage,
  }))
)

function withSuspense(Component: React.ComponentType) {
  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <RouteEnterContent>
        <Component />
      </RouteEnterContent>
    </Suspense>
  )
}

export const routes: RouteObject[] = [
  {
    path: "/unauthorized",
    element: <UnauthorizedPage />,
  },
  {
    path: "/ride/payment-methods",
    element: withSuspense(PaymentMethodPage),
  },
  {
    element: <AuthGate />,
    children: [
      {
        path: "/verification",
        element: withSuspense(VerificationPage),
      },
      {
        path: "/",
        element: <RootScreen />,
      },
      {
        path: "/ride/home/*",
        element: <HomePage />,
      },
      {
        path: "/ride/dropoff_location",
        element: withSuspense(SelectRoute),
      },
      {
        path: "/ride/dropoff_location/map",
        element: withSuspense(SelectRouteMap),
      },
      {
        path: "/ride/save_address_form/:type",
        element: withSuspense(SaveAddressFormScreen),
      },
      {
        path: "/ride/save_address/:type",
        element: withSuspense(AddSaveAddressScreen),
      },
      {
        path: "/ride/confirm-address",
        element: withSuspense(ConfirmAddress),
      },
      {
        path: "/ride/edit-pickup",
        element: withSuspense(EditPickupPage),
      },
      {
        path: "/ride/booking",
        element: withSuspense(FarePage),
      },
      {
        path: "/ride/in-trip",
        element: withSuspense(InRideTripPage),
      },
      {
        path: "/ride/intrip",
        element: withSuspense(InTripPreloadScreen),
      },
      {
        path: "/ride/booking/option",
        element: withSuspense(FareOptionScreen),
      },
      {
        path: "/ride/booking/option/invoice",
        element: withSuspense(InvoiceInformationScreen),
      },
      {
        path: "/ride/promo-select",
        element: withSuspense(PromoSelect),
      },
      {
        path: "/ride/promo/detail",
        element: withSuspense(PromoDetailPage),
      },
      {
        path: "/ride/cancel-engagement",
        element: withSuspense(CancelEngagement),
      },
      {
        path: "/ride/finish",
        element: withSuspense(FinishRide),
      },
      {
        path: "/ride/rating",
        element: withSuspense(RideRating),
      },
      {
        path: "/ride/chat",
        element: withSuspense(ChatScreen),
      },
      {
        path: "/profile/edit_location",
        element: withSuspense(EditUserLocationPage),
      },
      {
        path: "/profile/payment-methods",
        element: withSuspense(UserPaymentMethodScreen),
      },
      {
        path: "/profile/settings",
        element: withSuspense(UserSettingsPage),
      },
      {
        path: "/profile/settings/language",
        element: withSuspense(UserSettingsLanguagePage),
      },
      {
        path: "/activities/transport/detail/:engagementid/:sessionid",
        element: withSuspense(TransportDetailScreen),
      },
      {
        path: "/ride/contract",
        element: withSuspense(RideContractScreen),
      },
      {
        path: "/profile/support",
        element: withSuspense(UserSupportScreen),
      },
    ],
  },
  {
    path: "/activities/transport/detail/:engagementid",
    element: withSuspense(TransportDetailScreen),
  },
  {
    path: "*",
    element: withSuspense(NonSupportedPage),
  },
]
